import { StandardEvents } from '@shopify/events';
import { cardMinimumState } from './mf-card-minimum-rules.js';

class MFCardMinimum extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    this.request = (this.request || 0) + 1;
    this.configNode = this.querySelector('[data-minimum-config]');
    this.notice = this.querySelector('[data-minimum-notice]');
    this.heading = this.querySelector('[data-minimum-heading]');
    this.errors = this.querySelector('[data-minimum-errors]');
    this.retry = this.querySelector('[data-minimum-retry]');
    this.ctas = this.nextElementSibling;
    if (!this.configNode || !this.notice || !this.ctas?.matches('.cart__ctas')) return;
    this.buttonStates = new Map();
    this.pending = false;
    this.failed = false;
    this.readConfig();
    const options = { signal: this.controller.signal };
    document.addEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate, { ...options, capture: true });
    document.addEventListener('quantity-selector:update', this.onQuantity, { ...options, capture: true });
    document.addEventListener('input', this.onQuantity, { ...options, capture: true });
    document.addEventListener('submit', this.onSubmit, { ...options, capture: true });
    document.addEventListener('click', this.onClick, { ...options, capture: true });
    window.addEventListener('pageshow', this.onPageShow, options);
    this.addEventListener('click', event => {
      if (event.target instanceof Element && event.target.closest('[data-minimum-retry]')) this.refreshCart();
    }, options);
    this.configObserver = new MutationObserver(() => {
      const configNode = this.querySelector('[data-minimum-config]');
      if (configNode && (configNode !== this.configNode || configNode.textContent !== this.configText)) {
        this.configNode = configNode;
        this.readConfig();
      } else if (this.querySelector('[data-minimum-notice]') !== this.notice) {
        this.render();
      }
    });
    this.configObserver.observe(this, { childList: true, characterData: true, subtree: true });
    this.controlsObserver = new MutationObserver(() => this.render());
    this.controlsObserver.observe(this.ctas, { childList: true, subtree: true, attributes: true, attributeFilter: ['disabled'] });
    this.render();
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.configObserver?.disconnect();
    this.controlsObserver?.disconnect();
    this.request = (this.request || 0) + 1;
  }

  readConfig() {
    try {
      this.configText = this.configNode.textContent;
      this.config = JSON.parse(this.configNode.textContent);
      this.items = this.config.items;
      this.failed = false;
    } catch {
      this.failed = true;
      this.items ||= [];
    }
    this.render();
  }

  onQuantity = event => {
    const source = event.target;
    if (!(source instanceof Element)) return;
    const component = source.closest('cart-items-component');
    if (!component || (event.type === 'input' && !source.matches('input[name="updates[]"]'))) return;
    const rows = Array.from(component.querySelectorAll('tr[data-key]'));
    const edited = new Map(rows.map(row => {
      const input = row.querySelector('input[name="updates[]"]');
      return [row.dataset.key, input ? Number(input.value) : NaN];
    }));
    if (!this.items.some(item => edited.has(item.key))) return;
    this.items = this.items.map(item => edited.has(item.key) ? { ...item, quantity: edited.get(item.key) } : item);
    this.request += 1;
    this.pending = true;
    this.failed = false;
    this.render();
  };

  onCartUpdate = async event => {
    this.pending = true;
    this.render();
    try {
      if (event.promise) await event.promise;
    } catch {
      this.failed = true;
    }
    if (this.isConnected) this.refreshCart();
  };

  onPageShow = event => {
    if (event.persisted) this.refreshCart();
  };

  refreshCart = async () => {
    const request = ++this.request;
    this.pending = true;
    this.failed = false;
    this.render();
    try {
      const response = await fetch(this.config.cartUrl, {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'application/json' },
        signal: AbortSignal.any([this.controller.signal, AbortSignal.timeout(10000)]),
      });
      if (!response.ok) throw new Error();
      const cart = await response.json();
      if (!Array.isArray(cart.items)) throw new Error();
      if (!this.isConnected || request !== this.request) return;
      this.items = cart.items;
    } catch {
      if (!this.isConnected || request !== this.request) return;
      this.failed = true;
    } finally {
      if (this.isConnected && request === this.request) {
        this.pending = false;
        this.render();
      }
    }
  };

  onClick = event => {
    if (!this.blocked || !(event.target instanceof Element)) return;
    const control = event.target.closest('button[name="checkout"],input[name="checkout"],a[href]');
    if (!control || !this.ctas.contains(control)) return;
    if (control instanceof HTMLAnchorElement) {
      const url = new URL(control.href, location.href);
      if (!/\/checkout(?:\/|$)/.test(url.pathname)) return;
    }
    event.preventDefault();
    event.stopImmediatePropagation();
    this.heading.focus({ preventScroll: true });
    this.notice.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  };

  onSubmit = event => {
    if (!this.blocked || !(event.target instanceof HTMLFormElement)) return;
    const checkout = event.submitter;
    const ownSubmitter = checkout instanceof Element && this.ctas.contains(checkout);
    const cartForm = event.target.id === 'cart-form' && this.getClientRects().length > 0;
    if (!ownSubmitter && !cartForm) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    this.heading.focus({ preventScroll: true });
  };

  render() {
    if (!this.config || !this.ctas) return;
    this.notice = this.querySelector('[data-minimum-notice]');
    this.heading = this.querySelector('[data-minimum-heading]');
    this.errors = this.querySelector('[data-minimum-errors]');
    this.retry = this.querySelector('[data-minimum-retry]');
    const ctas = this.nextElementSibling;
    if (ctas?.matches('.cart__ctas') && ctas !== this.ctas) {
      this.ctas = ctas;
      this.controlsObserver?.disconnect();
      this.controlsObserver?.observe(ctas, { childList: true, subtree: true, attributes: true, attributeFilter: ['disabled'] });
    }
    if (!this.notice || !this.heading || !this.errors || !this.retry) return;
    const state = cardMinimumState(this.items || [], this.config.variants);
    this.blocked = this.pending || this.failed || state.blocked;
    this.notice.hidden = !this.blocked;
    const messages = this.config.messages;
    const heading = this.failed || state.invalid ? messages.failed : this.pending ? messages.updating : messages.heading;
    if (this.heading.textContent !== heading) this.heading.textContent = heading;
    const lines = state.errors.map(error => messages[error.format]
      .replace('MF_COUNT', String(error.count))
      .replace('MF_MISSING', String(error.missing)));
    if (this.errors.textContent !== lines.join('')) {
      this.errors.replaceChildren(...lines.map(text => {
        const item = document.createElement('li');
        item.textContent = text;
        return item;
      }));
    }
    this.retry.hidden = !this.failed;
    this.retry.disabled = this.pending;
    this.ctas.toggleAttribute('data-mf-minimum-blocked', this.blocked);
    for (const button of this.ctas.querySelectorAll('button[name="checkout"],input[name="checkout"]')) {
      if (!this.buttonStates.has(button)) {
        this.buttonStates.set(button, button.disabled && !button.hasAttribute('data-mf-minimum-disabled'));
      }
      const disabled = this.blocked || !this.items.length || this.buttonStates.get(button);
      if (button.disabled !== disabled) button.disabled = disabled;
      button.setAttribute('aria-disabled', String(disabled));
    }
  }
}

if (!customElements.get('mf-card-minimum')) customElements.define('mf-card-minimum', MFCardMinimum);
